import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog'; // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
import { Payable } from '../../../../../models/entities/payable';
import { UploadedFile } from '../../../../../models/entities/uploaded-file';
import { PayablesService } from '../../../../../services/payables/payables.service';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CommonModule } from '@angular/common';
import { PayableAttachmentCarouselComponent } from '../payable-attachment-carousel/payable-attachment-carousel.component';
import { finalize } from 'rxjs/operators';

export interface ViewInvoiceDialogData {
    payable: Payable;
}

@UntilDestroy()
@Component({
    selector: 'app-view-invoice-dialog',
    templateUrl: './view-invoice-dialog.component.html',
    styleUrls: ['./view-invoice-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        PayableAttachmentCarouselComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ViewInvoiceDialogComponent {
    isSubmitting = false;

    constructor(
            @Inject(MAT_DIALOG_DATA) public data: ViewInvoiceDialogData, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
            private _dialogRef: MatDialogRef<ViewInvoiceDialogComponent>, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
            private _payablesService: PayablesService,
            private _growlerService: GrowlerService
    ) {
        this._dialogRef.addPanelClass('kit-dialog');
    }

    get invoiceAttachment(): UploadedFile {
        if (!this.data.payable.attachments || !this.data.payable.invoiceUploadedFileId) {
            return null;
        }

        return this.data.payable.attachments.find(
                attachment => attachment.id === this.data.payable.invoiceUploadedFileId
        );
    }

    get vendorName(): string {
        return this.data.payable.vendor?.name || 'Unknown Vendor';
    }

    get invoiceNumber(): string {
        return this.data.payable.invoiceNumber || 'N/A';
    }

    closeDialog(): void {
        this._dialogRef.close();
    }

    downloadInvoice(): void {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;
        this._payablesService.downloadPayableInvoice(this.data.payable.id)
                .pipe(finalize(() => this.isSubmitting = false), untilDestroyed(this))
                .subscribe({
                    next: (response: any) => {
                        if (response.body && response.headers) {
                            const contentDisposition = response.headers.get('Content-Disposition');
                            let filename = `invoice_${this.data.payable.id}.pdf`;

                            if (contentDisposition) {
                                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                                if (matches != null && matches[1]) {
                                    filename = matches[1].replace(/['"]/g, '');
                                }
                            }

                            const blob = new Blob([response.body], { type: response.body.type });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = filename;
                            link.click();
                            window.URL.revokeObjectURL(url);
                        }
                    },
                    error: () => {
                        this._growlerService.oops('There was a problem downloading the invoice.');
                    }
                });
    }
}
